import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  DemographicProfile,
  StakeholderAdvies,
  BedrijvenData,
  BedrijvenAdvies,
  PersonaHint,
  AdditionalDemographics,
  OmgevingsData,
  VoorzieningenData,
  BedrijvenOverpassData,
  OrganisatiesData,
  Gebiedsprofiel,
  InvloedsgebiedAnalyse,
  WoningCategorie,
  WONING_CATEGORIE_LABELS,
  RaakvlakAnalyse,
} from '../../types';
import styles from './GebiedsanalyseModule.module.css';
import { OmgevingsDataSummary } from './OmgevingsDataSummary';
import VoorzieningenSection from './VoorzieningenSection';
import BedrijvenOverpassSection from './BedrijvenOverpassSection';
import OrganisatiesSection from './OrganisatiesSection';
import GebiedsprofielSection from './GebiedsprofielSection';
import RaakvlakprojectenSection from './RaakvlakprojectenSection';
import { CATEGORY_ICON_MAP, PinIcon } from './CategoryIcons';
import { GeraadpleegdeBronnen } from './GeraadpleegdeBronnen';

interface AnalysisSidebarProps {
  loading: boolean;
  buurtNaam: string | null;
  demographics: DemographicProfile | null;
  adviezen: StakeholderAdvies[];
  influenceRadius: number;
  areaSqm: number | null;
  onRadiusChange: (radius: number) => void;
  onRefresh: () => void;
  onDelete?: () => void;
  hasArea: boolean;
  markerCount?: number;
  bedrijven?: BedrijvenData | null;
  bedrijvenAdviezen?: BedrijvenAdvies[];
  personaHints?: PersonaHint[];
  additionalDemographics?: AdditionalDemographics | null;
  omgevingsData?: OmgevingsData | null;
  onMapCenter?: (lat: number, lng: number) => void;
  voorzieningen?: VoorzieningenData | null;
  bedrijvenOverpass?: BedrijvenOverpassData | null;
  organisaties?: OrganisatiesData | null;
  gebiedsprofiel?: Gebiedsprofiel | null;
  onRegenerateProfiel?: (customInstruction?: string) => void;
  profielLoading?: boolean;
  fullWidth?: boolean;
  invloedsgebiedAnalyse?: InvloedsgebiedAnalyse | null;
  invloedsgebiedLoading?: boolean;
  woningFilters?: WoningCategorie[];
  onWoningFilterChange?: (filters: WoningCategorie[]) => void;
  raakvlakAnalyse?: RaakvlakAnalyse | null;
  raakvlakLoading?: boolean;
  onImportToRelatiebeheer?: () => void;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: '#1E3A2F',
  medium: '#FF6B35',
  low: '#277CEA',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: '#1E3A2F',
  medium: '#FF6B35',
  low: '#9DA6A8',
};

const CATEGORY_LABELS: Record<string, string> = {
  kantoren: 'Kantoren',
  retail: 'Retail',
  industrie: 'Industrie',
  zorg: 'Zorg',
  onderwijs: 'Onderwijs',
  sport: 'Sport',
  horeca: 'Horeca',
  overig: 'Overig',
};

export const AnalysisSidebar: React.FC<AnalysisSidebarProps> = ({
  loading,
  buurtNaam,
  demographics,
  adviezen,
  influenceRadius,
  areaSqm,
  onRadiusChange,
  onRefresh,
  onDelete,
  hasArea,
  markerCount = 0,
  bedrijven,
  bedrijvenAdviezen = [],
  personaHints = [],
  additionalDemographics,
  omgevingsData,
  onMapCenter,
  voorzieningen,
  bedrijvenOverpass,
  organisaties,
  gebiedsprofiel,
  onRegenerateProfiel,
  profielLoading = false,
  fullWidth = false,
  invloedsgebiedAnalyse,
  invloedsgebiedLoading = false,
  woningFilters = [],
  onWoningFilterChange,
  raakvlakAnalyse,
  raakvlakLoading = false,
  onImportToRelatiebeheer,
}) => {
  if (loading) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Data ophalen van PDOK & CBS...</p>
        </div>
      </div>
    );
  }

  if (!hasArea) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.emptyState}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
          </svg>
          <h3>Teken een projectgebied</h3>
          <p>Gebruik de tekenhulpmiddelen rechts op de kaart om een polygoon, rechthoek of cirkel te tekenen. De gebiedsanalyse start automatisch.</p>
          <div className={styles.emptyHint}>
            <strong>Tip:</strong> Je kunt ook vlaggen plaatsen met de marker-tool om belangrijke locaties aan te duiden.
          </div>
        </div>
      </div>
    );
  }

  if (!demographics) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Gebiedsanalyse</h2>
          {buurtNaam && <p className={styles.buurtNaam}>{buurtNaam}</p>}
        </div>
        <div className={styles.emptyState}>
          <p>Geen demografische data beschikbaar voor dit gebied. De CBS data kan niet worden opgehaald voor deze locatie.</p>
          <button className={styles.refreshButton} onClick={onRefresh}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  const totalWoningen = (demographics.huurwoningen || 0) + (demographics.koopwoningen || 0);
  const huurPercentage = totalWoningen > 0 ? Math.round((demographics.huurwoningen / totalWoningen) * 100) : 0;
  const koopPercentage = totalWoningen > 0 ? 100 - huurPercentage : 0;

  const woningData = [
    { name: 'Huur', waarde: huurPercentage },
    { name: 'Koop', waarde: koopPercentage },
  ];

  const leeftijdData = [
    { name: '0-14', waarde: demographics.percentage0Tot14 },
    { name: '15-24', waarde: demographics.percentage15Tot24 },
    { name: '25-44', waarde: demographics.percentage25Tot44 },
    { name: '45-64', waarde: demographics.percentage45Tot64 },
    { name: '65+', waarde: demographics.percentage65Plus },
  ];


  return (
    <div className={`${styles.sidebar} ${fullWidth ? styles.sidebarFullWidth : ''}`}>
      <div className={styles.sidebarHeader}>
        <h2>Gebiedsanalyse</h2>
        {buurtNaam && <p className={styles.buurtNaam}>{buurtNaam}</p>}
      </div>

      {fullWidth ? (
        <div className={styles.fullWidthGrid}>
          <div className={styles.fullWidthCard}>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{demographics.inwoners.toLocaleString('nl-NL')}</span>
                <span className={styles.statLabel}>Inwoners</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{demographics.percentage65Plus}%</span>
                <span className={styles.statLabel}>65+</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {demographics.gemiddeldInkomen > 0
                    ? `${demographics.gemiddeldInkomen.toLocaleString('nl-NL')}k`
                    : '-'}
                </span>
                <span className={styles.statLabel}>Gem. Inkomen</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {areaSqm ? `${(areaSqm / 10000).toFixed(1)} ha` : '-'}
                </span>
                <span className={styles.statLabel}>Oppervlakte</span>
              </div>
            </div>
          </div>

          <div className={styles.fullWidthCard}>
            <div className={styles.section}>
              <h3>Invloedsgebied</h3>
              <div className={styles.radiusControl}>
                <input type="range" min={100} max={5000} step={100} value={influenceRadius}
                  onChange={(e) => onRadiusChange(parseInt(e.target.value, 10))} className={styles.rangeSlider} />
                <span className={styles.radiusValue}>{influenceRadius}m</span>
              </div>

              {/* Woningfilter chips */}
              {onWoningFilterChange && (
                <div className={styles.woningFilterChips}>
                  {(Object.keys(WONING_CATEGORIE_LABELS) as WoningCategorie[]).map(cat => {
                    const isActive = woningFilters.includes(cat);
                    return (
                      <button key={cat} className={`${styles.woningFilterChip} ${isActive ? styles.woningFilterChipActive : ''}`}
                        onClick={() => { const next = isActive ? woningFilters.filter(f => f !== cat) : [...woningFilters, cat]; onWoningFilterChange(next); }}>
                        {WONING_CATEGORIE_LABELS[cat]}
                      </button>
                    );
                  })}
                  {woningFilters.length > 0 && (
                    <button className={styles.woningFilterClear} onClick={() => onWoningFilterChange([])}>Wis filters</button>
                  )}
                </div>
              )}

              {invloedsgebiedLoading && (
                <div className={styles.invloedsgebiedLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Woninganalyse laden...</span>
                </div>
              )}
              {invloedsgebiedAnalyse && !invloedsgebiedLoading && (
                <div className={styles.invloedsgebiedAnalyse}>
                  {invloedsgebiedAnalyse.dichtstbijzijndeWoning && (
                    <div className={styles.invloedsHighlight}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      <div>
                        <span className={styles.invloedsLabel}>Dichtstbijzijnde woning{woningFilters.length > 0 ? ' (gefilterd)' : ''}</span>
                        <span className={styles.invloedsValue}>
                          {invloedsgebiedAnalyse.dichtstbijzijndeWoning.afstandMeter}m
                          <span className={styles.invloedsSubtext}> ({invloedsgebiedAnalyse.dichtstbijzijndeWoning.bouwperiode})</span>
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={styles.invloedsTotaal}>
                    <span className={styles.invloedsTotaalValue}>{invloedsgebiedAnalyse.totaalPandenInGebied.toLocaleString('nl-NL')}</span>
                    <span className={styles.invloedsTotaalLabel}>panden binnen {influenceRadius}m{woningFilters.length > 0 ? ' (gefilterd)' : ''}</span>
                  </div>
                  {invloedsgebiedAnalyse.categorieOverzicht && (
                    <div className={styles.categorieOverzicht}>
                      <h4>Woningen per categorie</h4>
                      {invloedsgebiedAnalyse.categorieOverzicht.filter(c => c.aantal > 0).sort((a, b) => b.aantal - a.aantal).map(cat => {
                        const maxAantal = Math.max(...invloedsgebiedAnalyse.categorieOverzicht.map(c => c.aantal), 1);
                        return (
                          <div key={cat.key} className={styles.invloedsWoningTypeRow}>
                            <span className={styles.invloedsWoningTypeName}>{cat.label}</span>
                            <div className={styles.invloedsWoningTypeBar}>
                              <div className={styles.invloedsWoningTypeBarFill} style={{ width: `${Math.min(100, (cat.aantal / maxAantal) * 100)}%` }} />
                            </div>
                            <span className={styles.invloedsWoningTypeCount}>{cat.aantal}</span>
                            {cat.dichtstbijMeter !== null && <span className={styles.catDistance}>{cat.dichtstbijMeter}m</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <table className={styles.invloedsZoneTable}>
                    <thead><tr><th>Zone</th><th>Panden</th><th>Type (meest voorkomend)</th></tr></thead>
                    <tbody>
                      {invloedsgebiedAnalyse.zones.map(zone => (
                        <tr key={zone.radius}>
                          <td className={styles.zoneLabel}>{zone.label}</td>
                          <td className={styles.zoneCount}>{zone.aantalPanden.toLocaleString('nl-NL')}</td>
                          <td className={styles.zoneType}>{zone.woningTypes.length > 0 ? zone.woningTypes[0].type : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {invloedsgebiedAnalyse.spreiding && invloedsgebiedAnalyse.totaalPandenInGebied > 0 && (
                    <div className={styles.spreidingSection}>
                      <h4>Ruimtelijke spreiding</h4>
                      <div className={styles.spreidingGrid}>
                        <div className={styles.spreidingCell} />
                        <div className={styles.spreidingCell}><span className={styles.spreidingDirection}>N</span><span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.noord}</span></div>
                        <div className={styles.spreidingCell} />
                        <div className={styles.spreidingCell}><span className={styles.spreidingDirection}>W</span><span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.west}</span></div>
                        <div className={`${styles.spreidingCell} ${styles.spreidingCenter}`}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg></div>
                        <div className={styles.spreidingCell}><span className={styles.spreidingDirection}>O</span><span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.oost}</span></div>
                        <div className={styles.spreidingCell} />
                        <div className={styles.spreidingCell}><span className={styles.spreidingDirection}>Z</span><span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.zuid}</span></div>
                        <div className={styles.spreidingCell} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.fullWidthCard}>
            <div className={styles.section}>
              <h3>Leeftijdsverdeling</h3>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={leeftijdData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis unit="%" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                    <Bar dataKey="waarde" fill="#1E3A2F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className={styles.fullWidthCard}>
            <div className={styles.section}>
              <h3>Woningtype Verdeling</h3>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={woningData} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <XAxis type="number" unit="%" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={40} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                    <Bar dataKey="waarde" fill="#277CEA" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {additionalDemographics && (
            <div className={styles.fullWidthCard}>
              <div className={styles.section}>
                <h3>Aanvullende Demografische Data</h3>
                <div className={styles.additionalStatsGrid}>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>{additionalDemographics.eenpersoonshuishoudens > 0 ? additionalDemographics.eenpersoonshuishoudens.toLocaleString('nl-NL') : '-'}</span>
                    <span className={styles.statLabel}>Eenpersoonshuishoudens</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>{additionalDemographics.huishoudensMetKinderen > 0 ? additionalDemographics.huishoudensMetKinderen.toLocaleString('nl-NL') : '-'}</span>
                    <span className={styles.statLabel}>Huishoudens met kinderen</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>{additionalDemographics.gemiddeldeWOZ > 0 ? `${Math.round(additionalDemographics.gemiddeldeWOZ / 1000)}k` : '-'}</span>
                    <span className={styles.statLabel}>Gem. WOZ-waarde</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>{additionalDemographics.percentageNietWesters > 0 ? `${additionalDemographics.percentageNietWesters}%` : '-'}</span>
                    <span className={styles.statLabel}>% Niet-westers</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {adviezen.length > 0 && (
            <div className={styles.fullWidthCard}>
              <div className={styles.section}>
                <h3>Stakeholder Adviezen</h3>
                <div className={styles.adviezenList}>
                  {adviezen.map((advies, i) => (
                    <div key={i} className={styles.adviesCard} style={{ borderLeftColor: SEVERITY_COLORS[advies.severity] }}>
                      <span className={styles.adviesBadge} style={{ backgroundColor: SEVERITY_COLORS[advies.severity] }}>{advies.type}</span>
                      <p>{advies.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={styles.fullWidthCard}>
            <GebiedsprofielSection data={gebiedsprofiel || null} onRegenerate={onRegenerateProfiel} loading={profielLoading} />
          </div>

          <div className={styles.fullWidthCard}>
            {voorzieningen ? (
              <VoorzieningenSection data={voorzieningen} onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined} />
            ) : (
              <div className={styles.section}>
                <h3>Voorzieningen (gem. afstand CBS)</h3>
                <div className={styles.facilityList}>
                  <div className={styles.facilityItem}><span>Huisarts</span><span>{demographics.afstandHuisarts > 0 ? `${demographics.afstandHuisarts} km` : '-'}</span></div>
                  <div className={styles.facilityItem}><span>Supermarkt</span><span>{demographics.afstandSupermarkt > 0 ? `${demographics.afstandSupermarkt} km` : '-'}</span></div>
                  <div className={styles.facilityItem}><span>Bevolkingsdichtheid</span><span>{demographics.bevolkingsdichtheid > 0 ? `${demographics.bevolkingsdichtheid}/km2` : '-'}</span></div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.fullWidthCard}>
            <OrganisatiesSection data={organisaties || null} onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined} />
          </div>

          {/* Import naar Relatiebeheer knop (fullWidth view) */}
          {onImportToRelatiebeheer && ((organisaties?.items?.length || 0) > 0 || (bedrijvenOverpass?.items?.length || 0) > 0) && (
            <div className={styles.fullWidthCard}>
              <button
                onClick={onImportToRelatiebeheer}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  background: 'white',
                  border: '1px solid #1E3A2F',
                  borderRadius: 8,
                  color: '#1E3A2F',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(30, 58, 47, 0.06)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Importeer naar Relatiebeheer
              </button>
            </div>
          )}

          <div className={styles.fullWidthCard}>
            <OmgevingsDataSummary data={omgevingsData || null} onItemClick={onMapCenter} />
          </div>

          <div className={styles.fullWidthCard}>
            <RaakvlakprojectenSection data={raakvlakAnalyse || null} loading={raakvlakLoading} onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined} />
          </div>

          <div className={styles.fullWidthCard}>
            <BedrijvenOverpassSection data={bedrijvenOverpass || null} onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined} />
          </div>

          <div className={styles.fullWidthCard}>
            <div className={styles.actions}>
              <button className={styles.refreshButton} onClick={onRefresh}>Data vernieuwen</button>
              {onDelete && (<button className={styles.deleteButton} onClick={onDelete}>Projectgebied verwijderen</button>)}
            </div>
          </div>
        </div>
      ) : (
        <>

      {/* Key Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{demographics.inwoners.toLocaleString('nl-NL')}</span>
          <span className={styles.statLabel}>Inwoners</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{demographics.percentage65Plus}%</span>
          <span className={styles.statLabel}>65+</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>
            {demographics.gemiddeldInkomen > 0
              ? `${demographics.gemiddeldInkomen.toLocaleString('nl-NL')}k`
              : '-'}
          </span>
          <span className={styles.statLabel}>Gem. Inkomen</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>
            {areaSqm ? `${(areaSqm / 10000).toFixed(1)} ha` : '-'}
          </span>
          <span className={styles.statLabel}>Oppervlakte</span>
        </div>
      </div>

      {/* Influence Radius */}
      <div className={styles.section}>
        <h3>Invloedsgebied</h3>
        <div className={styles.radiusControl}>
          <input
            type="range"
            min={100}
            max={5000}
            step={100}
            value={influenceRadius}
            onChange={(e) => onRadiusChange(parseInt(e.target.value, 10))}
            className={styles.rangeSlider}
          />
          <span className={styles.radiusValue}>{influenceRadius}m</span>
        </div>

        {/* Woningfilter chips */}
        {onWoningFilterChange && (
          <div className={styles.woningFilterChips}>
            {(Object.keys(WONING_CATEGORIE_LABELS) as WoningCategorie[]).map(cat => {
              const isActive = woningFilters.includes(cat);
              return (
                <button
                  key={cat}
                  className={`${styles.woningFilterChip} ${isActive ? styles.woningFilterChipActive : ''}`}
                  onClick={() => {
                    const next = isActive
                      ? woningFilters.filter(f => f !== cat)
                      : [...woningFilters, cat];
                    onWoningFilterChange(next);
                  }}
                >
                  {WONING_CATEGORIE_LABELS[cat]}
                </button>
              );
            })}
            {woningFilters.length > 0 && (
              <button
                className={styles.woningFilterClear}
                onClick={() => onWoningFilterChange([])}
              >
                Wis filters
              </button>
            )}
          </div>
        )}

        {/* Invloedsgebied Analyse Output */}
        {invloedsgebiedLoading && (
          <div className={styles.invloedsgebiedLoading}>
            <div className={styles.spinnerSmall} />
            <span>Woninganalyse laden...</span>
          </div>
        )}
        {invloedsgebiedAnalyse && !invloedsgebiedLoading && (
          <div className={styles.invloedsgebiedAnalyse}>
            {/* Dichtstbijzijnde woning */}
            {invloedsgebiedAnalyse.dichtstbijzijndeWoning && (
              <div className={styles.invloedsHighlight}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <div>
                  <span className={styles.invloedsLabel}>Dichtstbijzijnde woning{woningFilters.length > 0 ? ' (gefilterd)' : ''}</span>
                  <span className={styles.invloedsValue}>
                    {invloedsgebiedAnalyse.dichtstbijzijndeWoning.afstandMeter}m
                    <span className={styles.invloedsSubtext}> ({invloedsgebiedAnalyse.dichtstbijzijndeWoning.bouwperiode})</span>
                  </span>
                </div>
              </div>
            )}

            {/* Totaal */}
            <div className={styles.invloedsTotaal}>
              <span className={styles.invloedsTotaalValue}>{invloedsgebiedAnalyse.totaalPandenInGebied.toLocaleString('nl-NL')}</span>
              <span className={styles.invloedsTotaalLabel}>panden binnen {influenceRadius}m{woningFilters.length > 0 ? ' (gefilterd)' : ''}</span>
            </div>

            {/* Categorie overzicht */}
            {invloedsgebiedAnalyse.categorieOverzicht && (
              <div className={styles.categorieOverzicht}>
                <h4>Woningen per categorie</h4>
                {invloedsgebiedAnalyse.categorieOverzicht
                  .filter(c => c.aantal > 0)
                  .sort((a, b) => b.aantal - a.aantal)
                  .map(cat => {
                    const maxAantal = Math.max(...invloedsgebiedAnalyse.categorieOverzicht.map(c => c.aantal), 1);
                    return (
                      <div key={cat.key} className={styles.invloedsWoningTypeRow}>
                        <span className={styles.invloedsWoningTypeName}>{cat.label}</span>
                        <div className={styles.invloedsWoningTypeBar}>
                          <div
                            className={styles.invloedsWoningTypeBarFill}
                            style={{ width: `${Math.min(100, (cat.aantal / maxAantal) * 100)}%` }}
                          />
                        </div>
                        <span className={styles.invloedsWoningTypeCount}>{cat.aantal}</span>
                        {cat.dichtstbijMeter !== null && (
                          <span className={styles.catDistance}>{cat.dichtstbijMeter}m</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Zones tabel */}
            <table className={styles.invloedsZoneTable}>
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>Panden</th>
                  <th>Type (meest voorkomend)</th>
                </tr>
              </thead>
              <tbody>
                {invloedsgebiedAnalyse.zones.map(zone => (
                  <tr key={zone.radius}>
                    <td className={styles.zoneLabel}>{zone.label}</td>
                    <td className={styles.zoneCount}>{zone.aantalPanden.toLocaleString('nl-NL')}</td>
                    <td className={styles.zoneType}>
                      {zone.woningTypes.length > 0 ? zone.woningTypes[0].type : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Ruimtelijke spreiding */}
            {invloedsgebiedAnalyse.spreiding && invloedsgebiedAnalyse.totaalPandenInGebied > 0 && (
              <div className={styles.spreidingSection}>
                <h4>Ruimtelijke spreiding</h4>
                <div className={styles.spreidingGrid}>
                  <div className={styles.spreidingCell} />
                  <div className={styles.spreidingCell}>
                    <span className={styles.spreidingDirection}>N</span>
                    <span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.noord}</span>
                  </div>
                  <div className={styles.spreidingCell} />
                  <div className={styles.spreidingCell}>
                    <span className={styles.spreidingDirection}>W</span>
                    <span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.west}</span>
                  </div>
                  <div className={`${styles.spreidingCell} ${styles.spreidingCenter}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                    </svg>
                  </div>
                  <div className={styles.spreidingCell}>
                    <span className={styles.spreidingDirection}>O</span>
                    <span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.oost}</span>
                  </div>
                  <div className={styles.spreidingCell} />
                  <div className={styles.spreidingCell}>
                    <span className={styles.spreidingDirection}>Z</span>
                    <span className={styles.spreidingCount}>{invloedsgebiedAnalyse.spreiding.zuid}</span>
                  </div>
                  <div className={styles.spreidingCell} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Markers Info */}
      {markerCount > 0 && (
        <div className={styles.section}>
          <h3>Markeringen</h3>
          <p className={styles.markerInfo}>
            {markerCount} markering{markerCount !== 1 ? 'en' : ''} geplaatst op de kaart. Klik op een vlag om deze te bewerken of verwijderen.
          </p>
        </div>
      )}

      {/* Stakeholder Adviezen */}
      {adviezen.length > 0 && (
        <div className={styles.section}>
          <h3>Stakeholder Adviezen</h3>
          <div className={styles.adviezenList}>
            {adviezen.map((advies, i) => (
              <div
                key={i}
                className={styles.adviesCard}
                style={{ borderLeftColor: SEVERITY_COLORS[advies.severity] }}
              >
                <span
                  className={styles.adviesBadge}
                  style={{ backgroundColor: SEVERITY_COLORS[advies.severity] }}
                >
                  {advies.type}
                </span>
                <p>{advies.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leeftijdsverdeling Chart */}
      <div className={styles.section}>
        <h3>Leeftijdsverdeling</h3>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={leeftijdData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis unit="%" tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
              <Bar dataKey="waarde" fill="#1E3A2F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Woningtype Verdeling Chart */}
      <div className={styles.section}>
        <h3>Woningtype Verdeling</h3>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={woningData} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <XAxis type="number" unit="%" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={40} />
              <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
              <Bar dataKey="waarde" fill="#277CEA" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>


      {/* AI Gebiedsprofiel */}
      <GebiedsprofielSection
        data={gebiedsprofiel || null}
        onRegenerate={onRegenerateProfiel}
        loading={profielLoading}
      />

      {/* Voorzieningen (Overpass) */}
      {voorzieningen ? (
        <VoorzieningenSection
          data={voorzieningen}
          onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined}
        />
      ) : (
        <div className={styles.section}>
          <h3>Voorzieningen (gem. afstand CBS)</h3>
          <div className={styles.facilityList}>
            <div className={styles.facilityItem}>
              <span>Huisarts</span>
              <span>{demographics.afstandHuisarts > 0 ? `${demographics.afstandHuisarts} km` : '-'}</span>
            </div>
            <div className={styles.facilityItem}>
              <span>Supermarkt</span>
              <span>{demographics.afstandSupermarkt > 0 ? `${demographics.afstandSupermarkt} km` : '-'}</span>
            </div>
            <div className={styles.facilityItem}>
              <span>Bevolkingsdichtheid</span>
              <span>{demographics.bevolkingsdichtheid > 0 ? `${demographics.bevolkingsdichtheid}/km2` : '-'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Maatschappelijke Organisaties */}
      <OrganisatiesSection
        data={organisaties || null}
        onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined}
      />

      {/* Import naar Relatiebeheer knop */}
      {onImportToRelatiebeheer && ((organisaties?.items?.length || 0) > 0 || (bedrijvenOverpass?.items?.length || 0) > 0) && (
        <div className={styles.section}>
          <button
            onClick={onImportToRelatiebeheer}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              background: 'white',
              border: '1px solid #1E3A2F',
              borderRadius: 8,
              color: '#1E3A2F',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(30, 58, 47, 0.06)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            Importeer naar Relatiebeheer
          </button>
        </div>
      )}

      {/* Omgevingsdata Summary */}
      <OmgevingsDataSummary data={omgevingsData || null} onItemClick={onMapCenter} />

      {/* Raakvlakprojecten */}
      <RaakvlakprojectenSection
        data={raakvlakAnalyse || null}
        loading={raakvlakLoading}
        onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined}
      />

      {/* Bedrijvigheid */}
      {bedrijven && bedrijven.totaal > 0 && (
        <>
          <hr className={styles.sectionDivider} />
          <div className={styles.section}>
            <h3>Bedrijvigheid in projectgebied</h3>
            <div className={styles.bedrijvenOverview}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{bedrijven.totaal.toLocaleString('nl-NL')}</span>
                <span className={styles.statLabel}>Bedrijven totaal</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{bedrijven.dichtheidPerKm2.toLocaleString('nl-NL')}</span>
                <span className={styles.statLabel}>Per km&sup2;</span>
              </div>
            </div>

            {/* Category bar chart */}
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={Object.entries(bedrijven.categorieen)
                    .filter(([key]) => key !== 'overig')
                    .map(([key, value]) => ({
                      name: CATEGORY_LABELS[key] || key,
                      aantal: value,
                    }))
                    .sort((a, b) => b.aantal - a.aantal)}
                  layout="vertical"
                  margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                >
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 11 }}
                    width={70}
                  />
                  <Tooltip formatter={(value) => [`${value}`, 'Aantal']} />
                  <Bar dataKey="aantal" fill="#1E3A2F" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Category list with icons */}
            <div className={styles.bedrijvenCategoryList}>
              {Object.entries(bedrijven.categorieen)
                .sort(([, a], [, b]) => b - a)
                .map(([key, value]) => (
                  <div key={key} className={styles.bedrijvenCategoryItem}>
                    <span className={styles.categoryIcon}>{(() => { const IconComp = CATEGORY_ICON_MAP[key] || PinIcon; return <IconComp size={16} color="#9DA6A8" />; })()}</span>
                    <span className={styles.categoryName}>{CATEGORY_LABELS[key] || key}</span>
                    <span className={styles.categoryCount}>{value}</span>
                  </div>
                ))}
            </div>

            {/* Top 5 Sectoren */}
            {bedrijven.topSectoren && bedrijven.topSectoren.length > 0 && (
              <>
                <h3 style={{ marginTop: 16 }}>Top sectoren</h3>
                <div className={styles.topSectorenList}>
                  {bedrijven.topSectoren.slice(0, 5).map((sector, i) => (
                    <div key={i} className={styles.topSectorItem}>
                      <span>{i + 1}. {sector.naam}</span>
                      <span>{sector.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Bedrijven via Overpass/OSM */}
      <BedrijvenOverpassSection
        data={bedrijvenOverpass || null}
        onItemClick={onMapCenter ? (lat, lon) => onMapCenter(lat, lon) : undefined}
      />

      {/* Bedrijven Stakeholder Adviezen */}
      {bedrijvenAdviezen.length > 0 && (
        <div className={styles.section}>
          <h3>Stakeholder Signalen (Bedrijven)</h3>
          <div className={styles.adviezenList}>
            {bedrijvenAdviezen.map((advies, i) => (
              <div
                key={i}
                className={styles.adviesCard}
                style={{ borderLeftColor: SEVERITY_COLORS[advies.severity] }}
              >
                <span
                  className={styles.adviesBadge}
                  style={{ backgroundColor: SEVERITY_COLORS[advies.severity] }}
                >
                  {advies.type}
                </span>
                {advies.sector && (
                  <span
                    className={styles.adviesBadge}
                    style={{ backgroundColor: '#9DA6A8', marginLeft: 4 }}
                  >
                    {advies.sector}
                  </span>
                )}
                <p>{advies.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Persona Suggesties */}
      {personaHints.length > 0 && (
        <>
          <hr className={styles.sectionDivider} />
          <div className={styles.section}>
            <h3>Persona Suggesties</h3>
            <div className={styles.personaHintsList}>
              {personaHints.map((hint, i) => (
                <div
                  key={i}
                  className={styles.personaHintCard}
                  style={{ borderLeftColor: PRIORITY_COLORS[hint.priority] }}
                >
                  <div className={styles.personaHintHeader}>
                    <span className={styles.personaHintType}>{hint.type}</span>
                    <span
                      className={styles.personaHintPriority}
                      style={{ backgroundColor: PRIORITY_COLORS[hint.priority] }}
                    >
                      {hint.priority}
                    </span>
                    {hint.channel && (
                      <span className={styles.personaHintChannel}>{hint.channel}</span>
                    )}
                  </div>
                  <p className={styles.personaHintReason}>{hint.reason}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Aanvullende Demografische Data */}
      {additionalDemographics && (
        <>
          <hr className={styles.sectionDivider} />
          <div className={styles.section}>
            <h3>Aanvullende Demografische Data</h3>
            <div className={styles.additionalStatsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {additionalDemographics.eenpersoonshuishoudens > 0
                    ? additionalDemographics.eenpersoonshuishoudens.toLocaleString('nl-NL')
                    : '-'}
                </span>
                <span className={styles.statLabel}>Eenpersoonshuishoudens</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {additionalDemographics.huishoudensMetKinderen > 0
                    ? additionalDemographics.huishoudensMetKinderen.toLocaleString('nl-NL')
                    : '-'}
                </span>
                <span className={styles.statLabel}>Huishoudens met kinderen</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {additionalDemographics.gemiddeldeWOZ > 0
                    ? `${Math.round(additionalDemographics.gemiddeldeWOZ / 1000)}k`
                    : '-'}
                </span>
                <span className={styles.statLabel}>Gem. WOZ-waarde</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>
                  {additionalDemographics.percentageNietWesters > 0
                    ? `${additionalDemographics.percentageNietWesters}%`
                    : '-'}
                </span>
                <span className={styles.statLabel}>% Niet-westers</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <button className={styles.refreshButton} onClick={onRefresh}>
          Data vernieuwen
        </button>
        {onDelete && (
          <button className={styles.deleteButton} onClick={onDelete}>
            Projectgebied verwijderen
          </button>
        )}
      </div>
        </>
      )}

      <GeraadpleegdeBronnen
        buurtNaam={buurtNaam}
        demographics={demographics}
        additionalDemographics={additionalDemographics}
        bedrijven={bedrijven}
        voorzieningen={voorzieningen}
        bedrijvenOverpass={bedrijvenOverpass}
        organisaties={organisaties}
        gebiedsprofiel={gebiedsprofiel}
        invloedsgebiedAnalyse={invloedsgebiedAnalyse}
        raakvlakAnalyse={raakvlakAnalyse}
        omgevingsData={omgevingsData}
      />
    </div>
  );
};
